import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  MapPin,
  Users,
  Clock,
  CheckCircle,
  Timer,
  XCircle,
  RefreshCw,
  Crosshair
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { GoogleMap, Marker, Circle, InfoWindow } from '@react-google-maps/api';
import { useGoogleMapsLoader } from '@/components/GoogleMapView';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Status → coloured circular SVG marker (Google needs raw URL/icon, no DOM)
const statusMarkerIcon = (color) =>
  'data:image/svg+xml;charset=UTF-8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34">` +
      `<circle cx="17" cy="17" r="13" fill="${color}" stroke="white" stroke-width="3"/>` +
      `<path d="M17 17a4 4 0 100-8 4 4 0 000 8zm0 2c-3 0-9 1.5-9 4.5V26h18v-2.5c0-3-6-4.5-9-4.5z" fill="white" transform="scale(0.55) translate(14,14)"/>` +
      `</svg>`
  );

const OFFICE_MARKER_ICON =
  'data:image/svg+xml;charset=UTF-8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">` +
      `<circle cx="20" cy="20" r="16" fill="#D4AF37" stroke="white" stroke-width="3"/>` +
      `<path d="M14 13v14h12V13H20v-2H14v2zm-2 0h2v2h-2v-2zm0 4h2v2h-2v-2zm0 4h2v2h-2v-2z" fill="white" transform="scale(0.7) translate(4,4)"/>` +
      `</svg>`
  );

const formatDistance = (m) => {
  if (m === null || m === undefined) return null;
  return m < 1000 ? `${m}m` : `${(m / 1000).toFixed(1)}km`;
};

// Spread markers that sit on (almost) the same spot in a small circle so all are clickable
const spreadOverlapping = (employees) => {
  const groups = {};
  employees.forEach((e) => {
    const key = `${e.latitude.toFixed(4)},${e.longitude.toFixed(4)}`;
    (groups[key] = groups[key] || []).push(e);
  });
  const out = [];
  Object.values(groups).forEach((group) => {
    if (group.length === 1) {
      out.push({ ...group[0], displayLat: group[0].latitude, displayLng: group[0].longitude });
    } else {
      const r = 0.00014;
      group.forEach((e, i) => {
        const angle = (2 * Math.PI * i) / group.length;
        out.push({
          ...e,
          displayLat: e.latitude + r * Math.sin(angle),
          displayLng: e.longitude + r * Math.cos(angle)
        });
      });
    }
  });
  return out;
};

const AttendanceMapPage = () => {
  const [loading, setLoading] = useState(true);
  const [activeEmployeeId, setActiveEmployeeId] = useState(null);
  const [activeLocationId, setActiveLocationId] = useState(null);
  const { isLoaded: mapsLoaded } = useGoogleMapsLoader();
  const [mapData, setMapData] = useState([]);
  const [config, setConfig] = useState(null);
  const [locations, setLocations] = useState([]);
  const [stats, setStats] = useState({ present: 0, late: 0, absent: 0 });
  const mapRef = useRef(null);
  const didFitRef = useRef(false);

  const fetchMapData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [mapRes, configRes, todayRes, locationsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/hr/attendance/map-data`, { headers }),
        axios.get(`${API_BASE_URL}/hr/config`, { headers }),
        axios.get(`${API_BASE_URL}/hr/attendance/today`, { headers }),
        axios.get(`${API_BASE_URL}/hr/locations`, { headers })
      ]);

      setMapData(mapRes.data.data);
      setConfig(configRes.data);
      setLocations(locationsRes.data.locations || []);
      setStats({
        present: todayRes.data.present,
        late: todayRes.data.late,
        absent: todayRes.data.absent
      });
    } catch (error) {
      console.error('Error fetching map data:', error);
      toast.error('Failed to load map data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMapData();
    const interval = setInterval(fetchMapData, 30000);
    return () => clearInterval(interval);
  }, [fetchMapData]);

  const getMarkerIcon = (status) => {
    switch (status) {
      case 'on_time':
        return statusMarkerIcon('#22c55e');
      case 'late':
        return statusMarkerIcon('#eab308');
      case 'half_day':
        return statusMarkerIcon('#f97316');
      default:
        return statusMarkerIcon('#ef4444');
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'on_time': return 'On Time';
      case 'late': return 'Late';
      case 'half_day': return 'Half Day';
      default: return 'Unknown';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'on_time': return 'text-green-600';
      case 'late': return 'text-yellow-600';
      case 'half_day': return 'text-orange-600';
      default: return 'text-red-600';
    }
  };

  const spreadEmployees = useMemo(() => spreadOverlapping(mapData), [mapData]);

  // Fit map to all markers exactly once, so refreshes / clicks never reset zoom
  const fitBoundsOnce = useCallback((map) => {
    if (didFitRef.current || !map || !window.google) return;
    const bounds = new window.google.maps.LatLngBounds();
    let count = 0;
    locations.forEach((loc) => {
      bounds.extend({ lat: loc.latitude, lng: loc.longitude });
      count += 1;
    });
    mapData.forEach((emp) => {
      bounds.extend({ lat: emp.latitude, lng: emp.longitude });
      count += 1;
    });
    if (count === 0 && config?.office_latitude) {
      bounds.extend({ lat: config.office_latitude, lng: config.office_longitude });
      count += 1;
    }
    if (count === 0) return;
    didFitRef.current = true;
    if (count === 1) {
      map.setCenter(bounds.getCenter());
      map.setZoom(16);
    } else {
      map.fitBounds(bounds, 70);
    }
  }, [locations, mapData, config]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    fitBoundsOnce(map);
  }, [fitBoundsOnce]);

  useEffect(() => {
    if (mapRef.current) fitBoundsOnce(mapRef.current);
  }, [fitBoundsOnce]);

  const focusEmployee = (emp) => {
    setActiveLocationId(null);
    setActiveEmployeeId(emp.user_id);
    const spread = spreadEmployees.find((e) => e.user_id === emp.user_id);
    if (mapRef.current && spread) {
      mapRef.current.panTo({ lat: spread.displayLat, lng: spread.displayLng });
      if (mapRef.current.getZoom() < 16) mapRef.current.setZoom(16);
    }
  };

  const recenterMap = () => {
    didFitRef.current = false;
    if (mapRef.current) fitBoundsOnce(mapRef.current);
  };

  // Stable reference: computed once after first data load so the map never re-centers on refresh/clicks
  const initialCenter = useMemo(() => {
    if (locations.length > 0) return { lat: locations[0].latitude, lng: locations[0].longitude };
    if (config?.office_latitude) return { lat: config.office_latitude, lng: config.office_longitude };
    return { lat: 25.2048, lng: 55.2708 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  if (loading || !config) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wt-gold"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="attendance-map-page">
      <style>{`
        .att-marker-label {
          background: rgba(255,255,255,0.92);
          padding: 1px 7px;
          border-radius: 9px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.35);
          white-space: nowrap;
        }
      `}</style>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-wt-text-primary">Attendance Map</h1>
          <p className="text-wt-text-muted">
            Real-time employee check-in locations
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={recenterMap}
            variant="outline"
            className="border-wt-border text-wt-text-secondary hover:text-wt-gold"
            data-testid="map-recenter-btn"
          >
            <Crosshair className="h-4 w-4 mr-2" />
            Fit All
          </Button>
          <Button
            onClick={fetchMapData}
            variant="outline"
            className="border-wt-border text-wt-text-secondary hover:text-wt-gold"
            data-testid="map-refresh-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-wt-surface border-wt-border">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-green-500/20">
              <CheckCircle className="h-6 w-6 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-400">{stats.present}</p>
              <p className="text-sm text-wt-text-muted">Present</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-wt-surface border-wt-border">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-yellow-500/20">
              <Timer className="h-6 w-6 text-yellow-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-400">{stats.late}</p>
              <p className="text-sm text-wt-text-muted">Late</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-wt-surface border-wt-border">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-red-500/20">
              <XCircle className="h-6 w-6 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-400">{stats.absent}</p>
              <p className="text-sm text-wt-text-muted">Absent</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Map */}
      <Card className="bg-wt-surface border-wt-border overflow-hidden">
        <CardContent className="p-0">
          <div className="h-[500px] w-full" data-testid="attendance-google-map">
            {!process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? (
              <div className="h-full w-full flex items-center justify-center text-xs text-wt-text-muted bg-wt-subtle/30">
                Google Maps API key not configured
              </div>
            ) : !mapsLoaded ? (
              <div className="h-full w-full flex items-center justify-center text-sm text-wt-text-muted">
                Loading map…
              </div>
            ) : (
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={initialCenter}
                zoom={12}
                onLoad={onMapLoad}
                options={{
                  mapTypeControl: false,
                  streetViewControl: false,
                  fullscreenControl: true,
                  gestureHandling: 'greedy',
                  clickableIcons: false,
                }}
              >
                {/* All Office Locations */}
                {locations.map((loc) => (
                  <React.Fragment key={loc.id}>
                    <Marker
                      position={{ lat: loc.latitude, lng: loc.longitude }}
                      icon={{
                        url: OFFICE_MARKER_ICON,
                        scaledSize: new window.google.maps.Size(40, 40),
                        labelOrigin: new window.google.maps.Point(20, 50)
                      }}
                      label={{
                        text: loc.name || 'Office',
                        color: '#8a6d1a',
                        fontSize: '12px',
                        fontWeight: '700',
                        className: 'att-marker-label'
                      }}
                      onClick={() => { setActiveEmployeeId(null); setActiveLocationId(loc.id); }}
                    >
                      {activeLocationId === loc.id && (
                        <InfoWindow onCloseClick={() => setActiveLocationId(null)}>
                          <div className="text-center p-2 text-gray-900">
                            <p className="font-bold">{loc.name}</p>
                            {loc.address && <p className="text-sm text-gray-500">{loc.address}</p>}
                            <p className="text-sm text-gray-600">Allowed Radius: {loc.allowed_radius_meters}m</p>
                          </div>
                        </InfoWindow>
                      )}
                    </Marker>
                    <Circle
                      center={{ lat: loc.latitude, lng: loc.longitude }}
                      radius={loc.allowed_radius_meters}
                      options={{
                        strokeColor: '#D4AF37',
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        fillColor: '#D4AF37',
                        fillOpacity: 0.1,
                      }}
                    />
                  </React.Fragment>
                ))}

                {/* Fallback: Show config location if no locations defined */}
                {locations.length === 0 && config?.office_latitude && (
                  <>
                    <Marker
                      position={{ lat: config.office_latitude, lng: config.office_longitude }}
                      icon={{
                        url: OFFICE_MARKER_ICON,
                        scaledSize: new window.google.maps.Size(40, 40),
                        labelOrigin: new window.google.maps.Point(20, 50)
                      }}
                      label={{
                        text: 'Office',
                        color: '#8a6d1a',
                        fontSize: '12px',
                        fontWeight: '700',
                        className: 'att-marker-label'
                      }}
                      onClick={() => { setActiveEmployeeId(null); setActiveLocationId('config'); }}
                    >
                      {activeLocationId === 'config' && (
                        <InfoWindow onCloseClick={() => setActiveLocationId(null)}>
                          <div className="text-center p-2 text-gray-900">
                            <p className="font-bold">Office Location</p>
                            <p className="text-sm text-gray-600">
                              Allowed Radius: {config.allowed_radius_meters}m
                            </p>
                          </div>
                        </InfoWindow>
                      )}
                    </Marker>
                    <Circle
                      center={{ lat: config.office_latitude, lng: config.office_longitude }}
                      radius={config.allowed_radius_meters}
                      options={{
                        strokeColor: '#D4AF37',
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        fillColor: '#D4AF37',
                        fillOpacity: 0.1,
                      }}
                    />
                  </>
                )}

                {/* Employee Markers — name always visible below the pin */}
                {spreadEmployees.map((emp) => (
                  <Marker
                    key={emp.user_id}
                    position={{ lat: emp.displayLat, lng: emp.displayLng }}
                    icon={{
                      url: getMarkerIcon(emp.status),
                      scaledSize: new window.google.maps.Size(34, 34),
                      labelOrigin: new window.google.maps.Point(17, 44)
                    }}
                    label={{
                      text: emp.user_name?.split(' ')[0] || '?',
                      color: '#1f2937',
                      fontSize: '12px',
                      fontWeight: '600',
                      className: 'att-marker-label'
                    }}
                    onClick={() => { setActiveLocationId(null); setActiveEmployeeId(emp.user_id); }}
                  >
                    {activeEmployeeId === emp.user_id && (
                      <InfoWindow onCloseClick={() => setActiveEmployeeId(null)}>
                        <div className="p-2 min-w-[210px] text-gray-900">
                          <div className="flex items-center gap-3 mb-3">
                            {emp.face_image ? (
                              <img src={emp.face_image} alt={emp.user_name} className="h-12 w-12 rounded-full object-cover" />
                            ) : emp.profile_photo ? (
                              <img src={emp.profile_photo} alt={emp.user_name} className="h-12 w-12 rounded-full object-cover" />
                            ) : (
                              <div className="h-12 w-12 rounded-full bg-[#D4AF37] flex items-center justify-center">
                                <span className="text-white font-bold">{emp.user_name?.charAt(0)}</span>
                              </div>
                            )}
                            <div>
                              <p className="font-bold">{emp.user_name}</p>
                              <p className={`text-sm font-medium ${getStatusColor(emp.status)}`}>
                                {getStatusLabel(emp.status)}
                                {emp.work_type && emp.work_type !== 'office' ? ` · ${emp.work_type}` : ''}
                              </p>
                            </div>
                          </div>
                          <div className="space-y-1 text-sm">
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-gray-500" />
                              <span>
                                In: {new Date(emp.check_in_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                {emp.check_out_time ? ` · Out: ${new Date(emp.check_out_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-gray-500" />
                              <span>
                                {formatDistance(emp.distance_from_location) !== null
                                  ? `${formatDistance(emp.distance_from_location)} from ${emp.location_name || 'office'}`
                                  : `${emp.latitude.toFixed(5)}, ${emp.longitude.toFixed(5)}`}
                              </span>
                            </div>
                          </div>
                        </div>
                      </InfoWindow>
                    )}
                  </Marker>
                ))}
              </GoogleMap>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card className="bg-wt-surface border-wt-border">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-6">
            <span className="text-sm text-wt-text-muted font-medium">Legend:</span>
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded-full bg-green-500"></div>
              <span className="text-sm text-wt-text-secondary">On Time</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded-full bg-yellow-500"></div>
              <span className="text-sm text-wt-text-secondary">Late</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded-full bg-orange-500"></div>
              <span className="text-sm text-wt-text-secondary">Half Day</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded-full bg-[#D4AF37]"></div>
              <span className="text-sm text-wt-text-secondary">Office</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 border-2 border-dashed border-[#D4AF37] rounded-full"></div>
              <span className="text-sm text-wt-text-secondary">Allowed Radius ({config.allowed_radius_meters}m)</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Employee List */}
      <Card className="bg-wt-surface border-wt-border">
        <CardHeader>
          <CardTitle className="text-wt-text-primary flex items-center gap-2">
            <Users className="h-5 w-5 text-wt-gold" />
            Today's Check-ins ({mapData.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {mapData.length === 0 ? (
            <p className="text-center text-wt-text-muted py-8">No check-ins yet today</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {mapData.map((emp) => (
                <button
                  key={emp.user_id}
                  onClick={() => focusEmployee(emp)}
                  data-testid={`checkin-card-${emp.user_id}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-wt-bg border border-wt-border text-left hover:border-wt-gold transition-colors cursor-pointer"
                  title="Click to locate on map"
                >
                  <div className={`h-3 w-3 rounded-full shrink-0 ${
                    emp.status === 'on_time' ? 'bg-green-500' :
                    emp.status === 'late' ? 'bg-yellow-500' :
                    emp.status === 'half_day' ? 'bg-orange-500' : 'bg-red-500'
                  }`}></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-wt-text-primary truncate">
                      {emp.user_name}
                    </p>
                    <p className="text-xs text-wt-text-muted">
                      {new Date(emp.check_in_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {formatDistance(emp.distance_from_location) !== null && (
                        <span> · {formatDistance(emp.distance_from_location)} from {emp.location_name || 'office'}</span>
                      )}
                    </p>
                  </div>
                  <MapPin className="h-4 w-4 text-wt-text-muted shrink-0" />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AttendanceMapPage;
